import { FunctionComponent, useEffect, useState } from 'react';
import { Stack, FormControl, InputLabel, OutlinedInput, FormHelperText, Autocomplete, Box, TextField, Select, MenuItem, SelectChangeEvent, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import LocationService from '../../../shared/services/location/location.service';
import { ICountry } from '../../../shared/services/location/location.model';
import { IPostConversionSource } from '../../../shared/services/conversion-source/conversion-source.model';
import ConversionSourceService from '../../../shared/services/conversion-source/conversion-source.service';

export const AddConversionSourceDialog: FunctionComponent<{
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  conversionSourceId?: string,
  isSubmitting: boolean,
  onSubmit: (values: IPostConversionSource, resetForm: () => void, isEdit: boolean, conversionSourceId?: string) => void,
}> = ({
  open,
  closeHandler,
  isEdit,
  conversionSourceId,
  isSubmitting,
  onSubmit
}) => {
    //Service
    const conversionSourceService = new ConversionSourceService()
    const locationService = LocationService.getInstance()
    //State
    const [countries, setCountries] = useState<ICountry[]>([])
    //Variables
    const conversionTypes = [{ label: 'General Conversion Factors', value: 'conversion' }, { label: 'Spend Based Conversion Factors', value: 'spendBasedConversion' }]

    useEffect(() => {
      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    const conversionSourceSchema = Yup.object().shape({
      source: Yup.string().trim().required('Name is required'),
      conversionType: Yup.string().required('Conversion type is required'),
      countryId: Yup.string()
        .when('conversionType', {
          is: 'spendBasedConversion',
          then: (schema) => schema.required('Country is required for currency conversion'),
          otherwise: (schema) => schema.nullable(),
        }),
    })
    const formik = useFormik<IPostConversionSource>({
      validationSchema: conversionSourceSchema,
      enableReinitialize: false,
      initialValues: {
        source: '',
        conversionType: 'conversion',
        countryId: '',
      },
      onSubmit: (values, { resetForm }) => {
        if (values.countryId === '') {
          values.countryId = undefined as any
        }
        onSubmit(values, resetForm, isEdit, conversionSourceId)
      }
    })

    useEffect(() => {
      if (open && isEdit && conversionSourceId) {
        conversionSourceService.getOne(conversionSourceId, `?includeYears=true`)
          .then(response => {
            formik.setFieldValue('source', response.source)
            formik.setFieldValue('conversionType', response.conversionType)
            formik.setFieldValue('countryId', response.countryId)
          })
          .catch(() => { })
      }
    }, [open, isEdit, conversionSourceId])

    const selectCountryHandler = (e: SelectChangeEvent<ICountry | null>, value: ICountry | null) => {
      if (value) {
        formik.setValues({ ...formik.values, countryId: value.id })
      }
    }


    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title={isEdit ? 'Edit Conversion Source' : 'Add Conversion Source'}
        cancelHandler={closeHandler}
        submitHandler={formik.submitForm}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
      >
        <Stack sx={{ mt: 2, mb: 2 }}>
          <FormControl fullWidth error={formik.touched.source && Boolean(formik.errors.source)}>
            <InputLabel htmlFor="source">Name</InputLabel>
            <OutlinedInput
              id="source"
              label="Name"
              value={formik.values.source || ''}
              onChange={(e) => formik.setFieldValue('source', e.target.value)}
            />
            <FormHelperText>
              {formik.touched.source && formik.errors.source ? String(formik.errors.source) : ' '}
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <InputLabel htmlFor='conversionType'>Type</InputLabel>
            <Select
              id='conversionType' label='Type' name='conversionType'
              value={formik.values.conversionType || ''}
              onChange={formik.handleChange}
              error={formik.touched.conversionType && Boolean(formik.errors.conversionType)}
              sx={{ color: 'text.primary', }}>
              {conversionTypes.map((conversionType, index) =>
                <MenuItem key={index} value={conversionType.value}>{conversionType.label}</MenuItem>
              )}
            </Select>
            {formik.touched.conversionType && formik.errors.conversionType &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.conversionType ? <div>{formik.errors.conversionType as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
          {countries && <FormControl fullWidth variant='outlined' sx={{ mb: 2 }}>
            <Autocomplete
              id="countryId"
              options={countries.slice().sort((a, b) => (a.name === 'United Kingdom' ? -1 : b.name === 'United Kingdom' ? 1 : a.name.localeCompare(b.name)))}
              sx={{
                color: 'text.primary'
              }}
              getOptionLabel={(option) => `${option.name}`}
              renderOption={(props, option) => {
                const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                return (
                  <Box
                    component="li"
                    sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                    key={key}
                    {...restProps}
                  >
                    <img
                      loading="lazy"
                      width="20"
                      src={option.imageUrl}
                      alt="country flag"
                    />
                    {option.name}
                  </Box>
                )
              }}
              value={countries?.find(country => country.id === formik.values.countryId) || null}
              onChange={(e: any, value: ICountry | null) => selectCountryHandler(e, value)}
              renderInput={(params) => (
                <TextField
                  label='Country'
                  {...params}
                  error={formik.touched.countryId && Boolean(formik.errors.countryId)}
                />
              )}
            />
            {formik.touched.countryId && formik.errors.countryId &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.countryId ? <div>{formik.errors.countryId as string}</div> : null}
              </FormHelperText>
            }
            {formik.values.conversionType === 'spendBasedConversion' && <Typography variant='body1' sx={{ mb: 2, fontWeight: 'bold' }}>* Spend factors are priced in a currency. A source created here takes it from the selected country (a currency can also be declared directly on the source, as the EXIOBASE source does). Inflation adjustment needs a CPI index for that currency (see CPI coverage), or spend rows will carry a CPI warning</Typography>}
          </FormControl>}
        </Stack>
      </DialogWrapper>
    )
  }


